// src/pages/admin/alumni/ViewAlumniPage.jsx

import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Printer } from "lucide-react";
import "./ViewAlumniPage.css";

const API_BASE_URL = process.env.REACT_APP_BACKEND_API_URL;
const DEFAULT_AVATAR =
  "https://cdn-icons-png.flaticon.com/512/149/149071.png";

export default function ViewAlumniPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [alumni, setAlumni] = useState(null);
  const [loading, setLoading] = useState(true);

  // ==========================
  // Fetch Alumni by ID
  // ==========================
  useEffect(() => {
    const fetchAlumni = async () => {
      try {
        const res = await axios.get(
          `${API_BASE_URL}/current-alumni/${id}`
        );
        setAlumni(res.data);
      } catch (err) {
        console.error("Failed to fetch alumni:", err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchAlumni();
  }, [id]);

  const formatDate = (date) => {
    if (!date) return "-";
    return new Date(date).toLocaleDateString("en-US", {
      month: "long",
      day: "2-digit",
      year: "numeric",
    });
  };

  if (loading) return <div className="view-loading">Loading alumni data...</div>;
  if (!alumni) return <div className="view-loading">Alumni not found.</div>;

  const fullName = [
    alumni.title,
    alumni.firstName,
    alumni.middleName,
    alumni.lastName,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className="view-alumni-page">
      {/* =======================
          ACTION BAR
         ======================= */}
      <div className="view-action-bar no-print">
        <button className="btn-back" onClick={() => navigate(-1)}>
          <ArrowLeft size={16} /> Back
        </button>

        <button className="btn-print" onClick={() => window.print()}>
          <Printer size={16} /> Print
        </button>
      </div>

      {/* =======================
          PROFILE HEADER
         ======================= */}
      <div className="profile-header">
        <img
          src={alumni.photo || DEFAULT_AVATAR}
          alt="Alumni"
          onError={(e) => (e.target.src = DEFAULT_AVATAR)}
        />
        <h1>{fullName}</h1>
        <p className="alumni-id">{alumni.alumniID}</p>
      </div>

      {/* =======================
          SECTION: SYSTEM INFO
         ======================= */}
      <div className="info-card">
        <h2>System Information</h2>
        <div className="info-grid">
          <p><strong>Status:</strong> {alumni.status}</p>
          <p><strong>Registration Status:</strong> {alumni.registrationStatus}</p>
          <p><strong>System Role:</strong> {alumni.systemRole}</p>
          <p><strong>Position:</strong> {alumni.position}</p>
          <p><strong>Date Registered:</strong> {formatDate(alumni.dateRegistered)}</p>
          <p><strong>Last Edited:</strong> {formatDate(alumni.dateEdited)}</p>
        </div>
      </div>

      {/* =======================
          SECTION: PERSONAL INFO
         ======================= */}
      <div className="info-card">
        <h2>Personal Information</h2>
        <div className="info-grid">
          <p><strong>Gender:</strong> {alumni.gender}</p>
          <p><strong>Date of Birth:</strong> {formatDate(alumni.dateOfBirth)}</p>
          <p><strong>Email:</strong> {alumni.email}</p>
          <p><strong>Mobile Numbers:</strong> {alumni.mobileNumbers?.join(", ")}</p>
          <p><strong>Address:</strong> {alumni.address || "-"}</p>
          <p><strong>Marital Status:</strong> {alumni.maritalStatus || "-"}</p>
        </div>
      </div>

      {/* =======================
          SECTION: ACADEMIC INFO
         ======================= */}
      <div className="info-card">
        <h2>Academic Information</h2>
        <div className="info-grid">
          <p><strong>Residence Hall:</strong> {alumni.residenceHall || "-"}</p>
          <p><strong>Year of Admission:</strong> {alumni.yearOfAdmission || "-"}</p>
          <p><strong>Year of Graduation:</strong> {alumni.yearOfGraduation || "-"}</p>
          <p><strong>Area of Study:</strong> {alumni.areaOfStudy || "-"}</p>
          <p><strong>Associate School:</strong> {alumni.associateSchool || "-"}</p>
          <p><strong>Academic Status:</strong> {alumni.academicStatus || "-"}</p>
          <p><strong>Professional Status:</strong> {alumni.professionalStatus || "-"}</p>
        </div>
      </div>

      {/* =======================
          SECTION: DEPENDENTS
         ======================= */}
      {alumni.dependents?.length > 0 && (
        <div className="info-card">
          <h2>Dependents</h2>
          <div className="dependents-table">
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Relationship</th>
                  <th>Date of Birth</th>
                  <th>Location</th>
                </tr>
              </thead>
              <tbody>
                {alumni.dependents.map((dep, index) => (
                  <tr key={index}>
                    <td>{dep.name}</td>
                    <td>{dep.relationship}</td>
                    <td>{formatDate(dep.dateOfBirth)}</td>
                    <td>{dep.location}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* =======================
          SECTION: EMERGENCY INFO
         ======================= */}
      <div className="info-card">
        <h2>Emergency / Next of Kin</h2>
        <div className="info-grid">
          <p><strong>Next of Kin:</strong> {alumni.nextOfKinName || "-"}</p>
          <p><strong>Emergency Contact:</strong> {alumni.emergencyContactName || "-"}</p>
          <p><strong>Emergency Phone:</strong> {alumni.emergencyContactPhone || "-"}</p>
        </div>
      </div>
    </div>
  );
}
